/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
/* eslint-disable sonarjs/cognitive-complexity */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Divider, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { isEmpty } from 'lodash';

import { IServiceCategoryItem, IServiceItem } from '../../../../Jobs/Models/AddEditOneTimeJob.Model';

import { BoxShadow } from '../../../../../Shared/Constants/Style.Constants';

import MasterDataApiService from '../../../../../Shared/Services/MasterDataService';

import theme from '../../../../../Shared/Themes/theme';
import { Icons } from '../../../../../Shared/Constants/Icons';
import {
  buttonStyles,
  ErrorMsgWrapper,
  JobNameContainer,
  ServiceFieldWrapper,
} from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { Options } from '../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import WhiteTooltip from '../../../../../Shared/Components/Common/CommonStyle/Tooltip.Style';
import { IScheduleServicesProps } from './ModifyServices.Model';

const ScheduleServices: React.FC<IScheduleServicesProps> = ({
  service,
  setService,
  serviceTasks,
  setServiceTasks,
  customerId,
  existingServiceId,
  setCreateNewTask,
  setServiceName,
  serviceName,
  setServiceDescription,
  serviceDescription,
  createService,
  setCreateService,
  setServiceConfirmed,
  setIsTasksOpen,
  isTasksOpen,
  createNewTask,
  serviceConfirmed,
  setTaskConfirmed,
  createdService,
  setSelectedTaskObj,
  resetTasksAndSubTasks,
  resetSubTasks,
}) => {
  const [isServiceLoading, setIsServiceLoading] = useState(false);
  const [isSelectOpen, setSelectOpen] = useState(new Array(service?.length).fill(false));
  const [serviceOptions, setServiceOptions] = useState<IServiceCategoryItem[]>([]);
  const [selectedServices, setSelectedServices] = useState<any[]>([]);
  const [selectedTasksMap, setSelectedTasksMap] = useState<{
    [serviceId: string]: { taskId: any; taskName: any; taskDescription?: any } | null;
  }>({});

  const { serviceAndAreaSpecification, textFieldService, divider } = buttonStyles;

  const { t } = useTranslation(['taskManagement', 'jobManagement', 'oneTimeJob', 'altTexts']);

  const { textField, headingText } = buttonStyles;

  const getServiceCategories = async () => {
    try {
      setIsServiceLoading(true);
      const allServiceCategoryV2Response = await MasterDataApiService.getAllServiceCategoryV3(customerId);
      if (allServiceCategoryV2Response?.data) {
        const modifiedCategories = allServiceCategoryV2Response?.data?.map((categoryItem: any) => {
          return {
            categoryId: categoryItem?.serviceCategoryId,
            categoryName: categoryItem?.serviceCategoryName,
            unit: categoryItem?.unit,
          };
        });
        const uniqueCategories: IServiceCategoryItem[] = [
          ...new Map(modifiedCategories?.map((item: any) => [item?.categoryId, item])).values(),
        ] as IServiceCategoryItem[];
        uniqueCategories.sort((a: any, b: any) => a?.categoryName?.localeCompare(b?.categoryName));
        const filteredOptions = uniqueCategories.filter((option) => option.categoryId !== existingServiceId);
        setServiceOptions(filteredOptions);
        fetchTaskDetails(allServiceCategoryV2Response);
      }
      setIsServiceLoading(false);
    } catch (error) {
      console.error(t('jobManagement:errorFetchingServiceCategories'), error);
    }
  };

  const fetchTaskDetails = (allServiceCategoryV2Response: any) => {
    const uniqueTaskData = allServiceCategoryV2Response?.data?.reduce((acc: any, task: any) => {
      const { serviceCategoryId } = task;
      if (!acc[serviceCategoryId]) {
        acc[serviceCategoryId] = [];
      }
      acc[serviceCategoryId]?.push(task);
      return acc;
    }, {});
    setServiceTasks?.(uniqueTaskData);
  };

  const handleServiceSelectChange = useCallback(
    (selectedServiceId: string, value: string, prevSelectedService: any) => {
      const selectedService = serviceOptions?.find((serviceItem) => serviceItem?.categoryId === value);
      const modifiedServiceOptions = service?.map((serviceItem) => {
        if (serviceItem.id === selectedServiceId) {
          serviceItem.service = value ?? '';
          serviceItem.mdServiceId = selectedService?.categoryId ?? '';
          serviceItem.unit = selectedService?.unit ?? '';
          serviceItem.nameError = '';
          serviceItem.name = selectedService?.categoryName;
        }
        return serviceItem;
      });

      setSelectedServices((prevState) => {
        const filteredState = prevState.filter((serviceId) => serviceId !== prevSelectedService);

        if (selectedService?.categoryId) {
          return [...filteredState, selectedService.categoryId];
        }

        return filteredState;
      });

      setService(modifiedServiceOptions);
      setServiceConfirmed?.(modifiedServiceOptions?.[0]?.mdServiceId ? true : false);
    },
    [service, serviceOptions]
  );

  const handleSelectedTasks = (selectedTaskId: string, selectedServiceId: string) => {
    const allTasksForService = serviceTasks[selectedServiceId] || [];

    const selectedTask = allTasksForService.find(
      (task: any) => task.taskId === selectedTaskId || task.mdTaskId === selectedTaskId
    );

    if (!selectedTask) return;

    const taskObject = {
      taskId: selectedTask.taskId || selectedTask.mdTaskId,
      taskName: selectedTask.taskName,
      taskDescription: selectedTask.taskDescription,
    };

    setSelectedTasksMap((prev) => ({
      ...prev,
      [selectedServiceId]: taskObject,
    }));
    setSelectedTaskObj?.(taskObject);

    setTaskConfirmed?.(true);
    const modifiedServiceOptions = service?.map((serviceItem: IServiceItem) => {
      if (serviceItem.mdServiceId === selectedServiceId) {
        serviceItem.tasks = taskObject.taskName;
        serviceItem.tasksError = '';
      }
      return serviceItem;
    });

    setService(modifiedServiceOptions);
  };

  const toggleSelectOpen = (index: number) => {
    const updatedIsSelectOpen = [...isSelectOpen];
    updatedIsSelectOpen[index] = !updatedIsSelectOpen[index];
    setSelectOpen(updatedIsSelectOpen);
  };

  const renderSelectTasksField = (mdServiceId: string) => {
    const dropdownValues = serviceTasks[mdServiceId] || [];
    const defaultValues = selectedTasksMap[mdServiceId];

    const isDisabled = createNewTask;

    return (
      <>
        {!isEmpty(selectedServices) && selectedServices.includes(mdServiceId) && (
          <>
            <Divider sx={{ ...divider, margin: '1.5rem 0' }} />

            <Typography sx={{ ...headingText, marginTop: '2rem' }}>{t('taskManagement:Tasks')}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', marginBottom: '2.5rem' }}>
              {/* Dropdown */}
              <TextField
                select
                sx={{
                  ...textField,
                  opacity: isDisabled ? '0.5' : '1',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
                disabled={isDisabled}
                label={<span>{t('oneTimeJob:selectTasks')}</span>}
                value={defaultValues}
                onChange={(event) => handleSelectedTasks(event?.target?.value, mdServiceId)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setIsTasksOpen?.(!isTasksOpen)}
                        style={{
                          padding: 0,
                          opacity: isDisabled ? '0.5' : '1',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isDisabled}>
                        <img
                          src={isTasksOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                          alt={isTasksOpen ? 'down' : 'up'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                SelectProps={{
                  displayEmpty: true,
                  IconComponent: () => null,
                  renderValue: (selected) => {
                    if (!selected) {
                      return <span style={{ color: '#9e9e9e' }}>{t('taskManagement:SelectTask')}</span>;
                    }
                    const taskMap = selectedTasksMap[mdServiceId];
                    return taskMap && typeof taskMap === 'object' && 'taskName' in taskMap ? taskMap.taskName : '';
                  },
                }}>
                {dropdownValues && dropdownValues.length > 0 ? (
                  dropdownValues
                    .filter((task: any) => task !== null && task !== undefined)
                    .map((task: any) => (
                      <Options key={task.taskId || task.mdTaskId} value={task.taskId || task.mdTaskId}>
                        {task.taskName}
                      </Options>
                    ))
                ) : (
                  <Options disabled>{t('taskManagement:NoTasksFound')}</Options>
                )}
              </TextField>

              {/* Add New Task */}
              <Box
                sx={{
                  color: '#006B6B',
                  letterSpacing: '0.25px',
                  fontSize: theme.typography.h5?.fontSize,
                  fontWeight: theme.typography.fontWeightBold,
                  fontFamily: theme.typography.fontFamily,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 0.5,
                  opacity: isDisabled ? '0.5' : '1',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
                onClick={() => {
                  if (!isDisabled) {
                    setSelectedTasksMap((prev) => ({
                      ...prev,
                      [mdServiceId]: null,
                    }));
                    setSelectedTaskObj?.(null);
                    setCreateNewTask?.(true);
                    setTaskConfirmed?.(false);
                    resetSubTasks();
                  }
                }}>
                <img src={Icons.AddIcon} width={24} height={24} alt={t('altTexts:add')} />
                {t('taskManagement:AddNewTask')}
              </Box>
            </Box>

            {/* Task Confirmed Indicator */}
            {!isEmpty(defaultValues) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '1rem', color: '#006B6B' }}>
                <img src={Icons.CheckedIcon} width={20} height={20} alt="confirmed" />
                <Typography sx={{ fontWeight: 600 }}>{t('taskManagement:TaskConfirmed')}</Typography>
              </Box>
            )}
          </>
        )}
      </>
    );
  };

  useEffect(() => {
    getServiceCategories();
  }, []);

  return (
    <>
      {service?.map((serviceItem, index) => {
        const prevSelectedService = serviceItem?.service;

        return (
          <>
            <Box key={serviceItem?.id} sx={{ ...serviceAndAreaSpecification, flexWrap: 'wrap', rowGap: '2rem' }}>
              <ServiceFieldWrapper>
                <TextField
                  select
                  sx={{
                    pointerEvents: isServiceLoading ? 'none' : 'all',
                    ...textFieldService,
                    opacity: createService ? '0.5' : '1',
                    cursor: createService ? 'not-allowed' : 'pointer',
                  }}
                  label={
                    <span>
                      {t('oneTimeJob:selectService')} <span className="mandatory-field">*</span>
                    </span>
                  }
                  disabled={createService}
                  value={serviceItem?.mdServiceId}
                  onChange={(e) => handleServiceSelectChange(serviceItem?.id, e?.target?.value, prevSelectedService)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {index === 0 && isServiceLoading ? (
                          <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                  SelectProps={{
                    IconComponent: () =>
                      !isServiceLoading ? (
                        <button
                          onClick={() => toggleSelectOpen(index)}
                          disabled={createService}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: 0,
                          }}>
                          <img
                            src={isSelectOpen[index] ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                            alt={isSelectOpen[index] ? 'down' : 'up'}
                            onClick={() => toggleSelectOpen(index)}
                          />
                        </button>
                      ) : null,
                    open: isSelectOpen[index],
                    onOpen: () => toggleSelectOpen(index),
                    onClose: () => toggleSelectOpen(index),
                    MenuProps: {
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'center',
                      },
                      PaperProps: {
                        style: {
                          boxShadow: BoxShadow.BoxShadow,
                        },
                      },
                    },
                  }}>
                  {serviceOptions && serviceOptions.length > 0 ? (
                    serviceOptions.map((item: any) => {
                      const alreadySelected = selectedServices?.includes(item?.categoryId);

                      return (
                        <Options
                          key={item?.categoryId}
                          value={item?.categoryId}
                          style={{
                            opacity: alreadySelected ? 0.3 : undefined,
                            pointerEvents: alreadySelected ? 'none' : 'auto',
                          }}>
                          {item?.categoryName}
                        </Options>
                      );
                    })
                  ) : (
                    <Options disabled>{t('taskManagement:NoServicesFound')}</Options>
                  )}
                </TextField>
                <ErrorMsgWrapper>{serviceItem?.nameError}</ErrorMsgWrapper>
              </ServiceFieldWrapper>

              <Box
                sx={{
                  color: '#006B6B',
                  letterSpacing: '0.25px',
                  fontSize: theme.typography.h5?.fontSize,
                  fontWeight: theme.typography.fontWeightBold,
                  fontFamily: theme.typography.fontFamily,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 0.5,
                  opacity: createService || isServiceLoading ? '0.5' : '1',
                  cursor: createService || isServiceLoading ? 'not-allowed' : 'pointer',
                }}
                onClick={() => {
                  if (isServiceLoading) return;

                  const clearedService = service?.map((serviceItem) => {
                    serviceItem.mdServiceId = '';
                    serviceItem.service = '';
                    return serviceItem;
                  });
                  setService(clearedService);

                  setSelectedServices([]);

                  setCreateService?.(true);
                  setServiceConfirmed?.(serviceName && serviceDescription ? true : false);
                  resetTasksAndSubTasks();
                }}>
                <img src={Icons.AddIcon} width={24} height={24} alt={t('altTexts:add')} />
                {t('taskManagement:AddNewService')}
              </Box>
            </Box>
            {createService && (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                <JobNameContainer sx={{ marginBottom: '0rem' }}>
                  <TextField
                    sx={textField}
                    id="outlined-required"
                    label={
                      <span>
                        {t('taskManagement:ServiceName')}
                        <span className="mandatory-field"> *</span>
                      </span>
                    }
                    value={serviceName}
                    onChange={(event) => {
                      const value = event?.target?.value;
                      setServiceName?.(value);
                    }}
                    disabled={createdService}
                  />
                </JobNameContainer>
                <JobNameContainer sx={{ marginBottom: '0rem' }}>
                  <TextField
                    sx={textField}
                    id="outlined-required"
                    label={
                      <span>
                        {t('taskManagement:ServiceDescription')}
                        <span className="mandatory-field"> *</span>
                      </span>
                    }
                    value={serviceDescription}
                    onChange={(event) => {
                      const value = event?.target?.value;
                      setServiceDescription?.(value);
                    }}
                    disabled={createdService}
                  />
                </JobNameContainer>
                {!createdService && (
                  <JobNameContainer sx={{ marginBottom: '0rem' }} onClick={() => setCreateService?.(false)}>
                    <WhiteTooltip title={'Cancel'}>
                      <img src={Icons.CancelIcon} alt="Cancel" width={24} height={24} />
                    </WhiteTooltip>
                  </JobNameContainer>
                )}
              </Box>
            )}
            {serviceConfirmed && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '2rem', color: '#006B6B' }}>
                <img src={Icons.CheckedIcon} width={20} height={20} alt="confirmed" />
                <Typography sx={{ fontWeight: 600 }}>{t('taskManagement:ServiceConfirmed')}</Typography>
              </Box>
            )}
            {!createService && renderSelectTasksField(serviceItem?.mdServiceId)}
          </>
        );
      })}
    </>
  );
};

export default ScheduleServices;
